// Utility functions for Google Analytics event tracking

// Declare gtag as a global function
declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
    dataLayer: any[];
  }
}

// Ensure we're on the client side and only track in production
const isClient = typeof window !== 'undefined';
const isProduction = process.env.NODE_ENV === 'production';
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '';

/**
 * Track a custom event in Google Analytics
 * 
 * @param {string} eventName - Name of the event to track
 * @param {Record<string, any>} [params] - Additional parameters to send with the event
 */
export const trackEvent = (eventName: string, params: Record<string, any> = {}) => {
  // Skip if not in production, not on client, or GA is not configured
  if (!isClient || !isProduction || !GA_MEASUREMENT_ID) return;

  // Skip if gtag is not loaded yet
  if (typeof window.gtag !== 'function') {
    console.warn('Google Analytics not loaded yet.');
    return;
  }

  // Track the event
  window.gtag('event', eventName, params);
};

/**
 * Track a page view in Google Analytics
 * 
 * @param {string} url - URL of the page to track
 * @param {Record<string, any>} [params] - Additional parameters to send with the pageview
 */
export const trackPageView = (url: string, params: Record<string, any> = {}) => {
  if (!isClient || !isProduction || !GA_MEASUREMENT_ID) return;
  
  if (typeof window.gtag !== 'function') {
    console.warn('Google Analytics not loaded yet.');
    return;
  }

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: url,
    ...params,
  });
};

/**
 * Track user conversion
 * 
 * @param {string} conversionId - ID of the conversion
 * @param {string} label - Label for the conversion
 * @param {Record<string, any>} [params] - Additional parameters for the conversion
 */
export const trackConversion = (
  conversionId: string,
  label: string,
  params: Record<string, any> = {}
) => {
  if (!isClient || !isProduction || !GA_MEASUREMENT_ID) return;

  trackEvent('conversion', {
    send_to: `${GA_MEASUREMENT_ID}/${conversionId}/${label}`,
    ...params,
  });
};

/**
 * Example usage:
 * 
 * // Import in your component
 * import { trackEvent } from '@/app/utils/analytics';
 * 
 * // Track a button click
 * const handleButtonClick = () => {
 *   trackEvent('button_click', { button_id: 'contact_us', page: 'home' });
 * };
 */ 